// ==============================================
// Admin › NMEA Studio
// ==============================================
//
// The full NMEA Studio, ported from the standalone Tauri desktop app in
// vortexnav-client/nmea2000-emulator. Map, vessel, route, weather, polars,
// faults, AIS, scenarios and transports — everything the desktop app does.
//
// The components and stores are the originals, unchanged. Only the transport
// was replaced: `invoke()` and the Tauri dialog/fs plugins now resolve to
// features/nmea-studio/tauriBridge, which speaks HTTP through the
// authenticated vortex-api relay. See that file for the command map.
//
// Layout follows the desktop app: config rail left, map centre with transport
// controls beneath, live data right. It takes the whole viewport — the console
// sidebar and header cost ~275px of width and ~90px of height, which is enough
// to make the config rail scroll sideways and clip the dashboard. The route is
// mounted outside AdminLayout; the header carries the way back.
//
// @docs: docs/core/kb/NMEA_BLE_ACQUISITION_NAVLINK_BLUE.md

import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import '../../features/nmea-studio/studio.css';
import { useSimulationStore } from '../../features/nmea-studio/stores/simulationStore';
import MapView from '../../features/nmea-studio/components/Map/MapView';
import ConfigPanel from '../../features/nmea-studio/components/Config/ConfigPanel';
import Dashboard from '../../features/nmea-studio/components/Dashboard/Dashboard';
import TransportControls from '../../features/nmea-studio/components/Controls/TransportControls';
import ConnectionStatus from '../../features/nmea-studio/components/Controls/ConnectionStatus';
import ServerInfo from '../../features/nmea-studio/components/Controls/ServerInfo';
import InstanceTabBar from '../../features/nmea-studio/components/Controls/InstanceTabBar';

/** Poll cadence for simulation state and NMEA data, matching the desktop app. */
const POLL_MS = 100;

/** Consecutive failed polls before we tell the operator the server is gone. */
const SERVER_DOWN_AFTER = 5;

export function AdminNmeaStudioPage() {
  const {
    fetchSimulationState,
    fetchNmeaData,
    fetchActiveTransportStatus,
    fetchFaultCatalog,
    state,
    restoreRoute,
    activeFaultCount,
  } = useSimulationStore();

  useEffect(() => {
    restoreRoute();
    fetchFaultCatalog();
  }, [restoreRoute, fetchFaultCatalog]);

  const [pollErrors, setPollErrors] = useState(0);
  const serverDown = pollErrors >= SERVER_DOWN_AFTER;

  // Sequential polling — each pass waits for the previous to finish, so a slow
  // relay backs the loop off instead of queueing requests behind itself.
  const stateAliveRef = useRef(true);
  useEffect(() => {
    stateAliveRef.current = true;
    let timer: ReturnType<typeof setTimeout>;

    const poll = async () => {
      try {
        await fetchSimulationState();
        await fetchActiveTransportStatus();
        setPollErrors(0);
      } catch {
        setPollErrors((n) => n + 1);
      }
      if (stateAliveRef.current) timer = setTimeout(poll, POLL_MS);
    };
    poll();

    return () => {
      stateAliveRef.current = false;
      clearTimeout(timer);
    };
  }, [fetchSimulationState, fetchActiveTransportStatus]);

  const nmeaAliveRef = useRef(true);
  useEffect(() => {
    if (state?.status !== 'running') return;

    nmeaAliveRef.current = true;
    let timer: ReturnType<typeof setTimeout>;

    const poll = async () => {
      try {
        await fetchNmeaData();
      } catch {
        /* NMEA send failures don't affect UI state */
      }
      if (nmeaAliveRef.current) timer = setTimeout(poll, POLL_MS);
    };
    poll();

    return () => {
      nmeaAliveRef.current = false;
      clearTimeout(timer);
    };
  }, [state?.status, fetchNmeaData]);

  return (
    <div className="nmea-studio flex h-screen w-screen flex-col overflow-hidden bg-nautical-darker">
      {/* Header */}
      <header className="relative z-[1000] flex h-12 flex-shrink-0 items-center justify-between border-b border-nautical-border bg-[#0f1729] px-4">
        <div className="flex items-center space-x-3">
          <Link
            to="/admin"
            className="flex items-center gap-1 rounded px-2 py-1 text-sm text-gray-400 transition hover:bg-white/5 hover:text-gray-200"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Admin
          </Link>
          <span className="h-4 w-px bg-nautical-border" aria-hidden="true" />
          <svg className="h-5 w-5" viewBox="0 0 100 100" fill="none" aria-hidden="true">
            <path d="M50 5 L20 45 L35 45 L15 95 L50 50 L35 50 Z" fill="#3b82f6" />
            <path d="M50 5 L80 45 L65 45 L85 95 L50 50 L65 50 Z" fill="#1e3a5f" />
          </svg>
          <span className="text-base font-medium text-gray-300">NMEA Studio</span>
        </div>
        <ServerInfo />
        <ConnectionStatus />
      </header>

      {serverDown && (
        <div className="flex-shrink-0 bg-red-900/80 px-4 py-1 text-center text-sm text-red-200">
          Server unreachable — data may be stale
        </div>
      )}

      <InstanceTabBar />

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        <aside className="w-72 flex-shrink-0 overflow-y-auto border-r border-nautical-border bg-nautical-dark">
          <ConfigPanel />
        </aside>

        <main className="flex flex-1 flex-col overflow-hidden">
          <div className="relative flex-1">
            <MapView />
          </div>
          <TransportControls />
        </main>

        <aside className="w-80 flex-shrink-0 overflow-y-auto border-l border-nautical-border bg-nautical-dark">
          <Dashboard />
        </aside>
      </div>

      {/* Status bar */}
      <footer className="flex h-8 flex-shrink-0 items-center justify-between border-t border-nautical-border bg-nautical-panel px-4 text-sm text-gray-500">
        <div className="flex items-center space-x-3">
          <span className={state?.status === 'running' ? 'text-green-500' : ''}>
            {state?.status || 'stopped'}
          </span>
          {state?.status === 'running' && state.speed_multiplier !== 1 && (
            <span className="text-gray-400">{state.speed_multiplier}x</span>
          )}
        </div>
        <div className="flex items-center space-x-4">
          {activeFaultCount > 0 && (
            <span className="text-red-400">
              Faults: <span className="font-medium">{activeFaultCount}</span>
            </span>
          )}
          <span>
            TX Rate: <span className="text-gray-200">10 Hz</span>
          </span>
          <span>
            Weather:{' '}
            <span className="capitalize text-gray-200">{state?.weather_source || 'manual'}</span>
          </span>
        </div>
      </footer>
    </div>
  );
}
